'use client'

import { useQueries } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

import { sumPortfolioSeries } from '@/features/dashboard/lib/portfolio-chart-aggregate'
import { type PerformanceSummary, summarizePerformance } from '@/features/home/lib/home-performance'
import { useUserAccounts } from '@/service/account.hook'
import { portfolioLineChartListQueryOptions } from '@/service/portfolio-line-chart.hook'
import { AccountStatus } from '@/types/generated/common/account'
import type { TradeVenue } from '@/types/generated/common/types'

const DAY_S = 86_400
/** One extra day before the window, so the first day has a change too. */
const WINDOW_DAYS = 30
const HOURLY = 3600

/**
 * The account's last 30 days from its hourly equity curve (every active account, summed per
 * venue as Account Cards does), boiled down to a day-by-day record.
 */
export function useHomePerformance(): { summary: PerformanceSummary | null; isPending: boolean } {
  const { data } = useUserAccounts()
  const [range] = useState(() => {
    const now = Date.now()
    return { now, from: String(Math.floor(now / 1000) - (WINDOW_DAYS + 1) * DAY_S) }
  })

  const groups = useMemo(() => {
    const byVenue = new Map<TradeVenue, string[]>()
    for (const account of data?.accounts ?? []) {
      if (account.status !== AccountStatus.ACCOUNT_STATUS_ACTIVE) continue
      byVenue.set(account.tradeVenue, [
        ...(byVenue.get(account.tradeVenue) ?? []),
        account.accountId,
      ])
    }
    return [...byVenue.entries()]
  }, [data?.accounts])

  const { charts, isPending } = useQueries({
    queries: groups.map(([tradeVenue, accountIds]) => ({
      ...portfolioLineChartListQueryOptions({
        accountIds,
        tradeVenue,
        timeframe: HOURLY,
        from: range.from,
      }),
      enabled: accountIds.length > 0,
    })),
    combine: (results) => ({
      charts: results.flatMap((result) => result.data ?? []),
      isPending: results.some((result) => result.isPending),
    }),
  })

  const summary = useMemo(() => {
    if (charts.length === 0) return null
    const series = sumPortfolioSeries(charts)
    return summarizePerformance(series.ticks, series.values, WINDOW_DAYS, range.now)
  }, [charts, range.now])

  return { summary, isPending: groups.length > 0 && isPending }
}
